import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt, WhatsappLogo, EnvelopeSimple, TelegramLogo, Broadcast } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import Paginator from "@/components/Paginator";
import { buildWaMeLink } from "@/pages/WhatsApp";
import { X, ExternalLink, Pause, Play, SkipForward, StopCircle, Loader2 } from "lucide-react";

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [pg, setPg] = useState({ page: 1, pages: 1, total: 0 });
  const [selected, setSelected] = useState([]);
  const [channel, setChannel] = useState("all");
  const [waMode, setWaMode] = useState("baileys"); // baileys | wame — só usado quando channel=whatsapp
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  // Fila wa.me
  const [queue, setQueue] = useState(null); // { items: [{lead, link}], idx, paused, delayMs, timer }

  const load = async (p = 1) => {
    try {
      const { data } = await api.get(`/leads?page=${p}&per_page=10`);
      setLeads(data.leads || []);
      setPg({ page: data.page || 1, pages: data.pages || 1, total: data.total || 0 });
      setSelected([]);
    } catch (e) {
      toast.error(errMsg(e, "Erro ao carregar leads"));
    }
  };
  useEffect(() => { load(1); }, []);

  const toggle = (id) =>
    setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const toggleAll = () =>
    setSelected(selected.length === leads.length ? [] : leads.map((l) => l.id));

  const renderMessage = (tpl, lead) =>
    String(tpl || "")
      .replaceAll("{nome}", lead?.name || "")
      .replaceAll("{email}", lead?.email || "")
      .replaceAll("{telefone}", lead?.phone || "");

  const selectedLeadObjs = useMemo(
    () => leads.filter((l) => selected.includes(l.id)),
    [leads, selected]
  );

  const send = async () => {
    if (!message) return toast.error("Escreva uma mensagem");
    if (selected.length === 0) return toast.error("Selecione ao menos 1 lead");

    // Fluxo especial: canal WhatsApp + modo wa.me → abre fila no frontend, sem backend
    if (channel === "whatsapp" && waMode === "wame") {
      const items = selectedLeadObjs
        .filter((l) => l.phone)
        .map((l) => ({ lead: l, link: buildWaMeLink(l.phone, renderMessage(message, l)) }));
      if (items.length === 0) return toast.error("Nenhum lead selecionado tem telefone");
      setQueue({ items, idx: 0, paused: false, delayMs: 5000, sent: 0 });
      // Abre a primeira aba imediatamente (user gesture — permite popup)
      window.open(items[0].link, "_blank", "noopener,noreferrer");
      toast.success(`Fila iniciada: ${items.length} contatos. Próximo em 5s.`);
      return;
    }

    // Fluxo especial: canal WhatsApp + modo Baileys → envia via sessão QR conectada
    if (channel === "whatsapp" && waMode === "baileys") {
      const items = selectedLeadObjs
        .filter((l) => l.phone)
        .map((l) => ({ to: l.phone, text: renderMessage(message, l) }));
      if (items.length === 0) return toast.error("Nenhum lead selecionado tem telefone");
      setSending(true);
      try {
        const { data } = await api.post("/whatsapp/baileys/send-bulk", { items, delay_ms: 3000 });
        const ok = (data.results || []).filter((r) => r.ok).length;
        toast.success(`WhatsApp: ${ok}/${items.length} enviados`);
        setMessage("");
        setSelected([]);
      } catch (err) {
        toast.error(errMsg(err, "Erro no envio Baileys"));
      } finally { setSending(false); }
      return;
    }

    // Fluxo padrão (email, telegram, all)
    setSending(true);
    try {
      const { data } = await api.post("/campaigns/send", { channel, subject, message, lead_ids: selected });
      toast.success(`Campanha enviada para ${data.results.length} leads`);
      setMessage("");
      setSubject("");
      setSelected([]);
    } catch (err) {
      toast.error(errMsg(err, "Erro"));
    } finally { setSending(false); }
  };

  // Loop da fila wa.me
  useEffect(() => {
    if (!queue || queue.paused) return;
    if (queue.idx >= queue.items.length - 1) return;
    const t = setTimeout(() => {
      const nextIdx = queue.idx + 1;
      const item = queue.items[nextIdx];
      try {
        window.open(item.link, "_blank", "noopener,noreferrer");
      } catch (_e) {
        toast.error("Bloqueado pelo navegador. Permita popups para este site.");
      }
      setQueue((q) => q ? { ...q, idx: nextIdx, sent: q.sent + 1 } : q);
    }, queue.delayMs);
    return () => clearTimeout(t);
  }, [queue]);

  const stopQueue = () => setQueue(null);
  const pauseQueue = () => setQueue((q) => q ? { ...q, paused: true } : q);
  const resumeQueue = () => setQueue((q) => q ? { ...q, paused: false } : q);
  const skipQueue = () => {
    if (!queue) return;
    if (queue.idx >= queue.items.length - 1) return stopQueue();
    const nextIdx = queue.idx + 1;
    window.open(queue.items[nextIdx].link, "_blank", "noopener,noreferrer");
    setQueue((q) => q ? { ...q, idx: nextIdx, sent: q.sent + 1 } : q);
  };

  const inp = "w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-4 py-3 focus:border-[var(--accent)] outline-none";

  return (
    <div className="fadeUp">
      <div className="label-caps mb-2">CRM leve</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-10">Leads & Campanhas</h1>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-flat">
          <div className="p-4 border-b border-[var(--border)] flex justify-between items-center">
            <div>
              <div className="label-caps">Leads capturados</div>
              <div className="font-display font-bold text-lg" data-testid="leads-total">{pg.total} contatos</div>
            </div>
            <button onClick={toggleAll} className="btn-ghost text-xs" data-testid="toggle-all-leads">
              {selected.length === leads.length && leads.length > 0 ? "Desmarcar todos" : "Selecionar todos"}
            </button>
          </div>
          <div className="divide-y divide-[var(--border)] max-h-[500px] overflow-y-auto">
            {leads.length === 0 && (
              <div className="p-6 text-white/50 text-sm" data-testid="no-leads">
                Nenhum lead ainda. Compartilhe seus links de venda para começar a capturar.
              </div>
            )}
            {leads.map((l) => (
              <label
                key={l.id}
                className="p-4 flex items-center gap-3 hover:bg-white/[0.02] cursor-pointer"
                data-testid={`lead-${l.id}`}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(l.id)}
                  onChange={() => toggle(l.id)}
                  className="accent-[var(--accent)]"
                />
                <div className="flex-1">
                  <div className="font-semibold text-sm">{l.name}</div>
                  <div className="text-xs text-white/50 flex gap-3 mt-1 flex-wrap">
                    <span className="flex items-center gap-1"><EnvelopeSimple size={12} /> {l.email}</span>
                    {l.phone && (
                      <span className="flex items-center gap-1"><WhatsappLogo size={12} /> {l.phone}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-white/40">
                  <span>{new Date(l.created_at).toLocaleDateString("pt-BR")}</span>
                  {l.phone && (
                    <a
                      href={buildWaMeLink(l.phone, renderMessage(message || `Olá ${l.name}, tudo bem?`, l))}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="px-2 py-1 border border-emerald-500/30 text-emerald-400 rounded-sm hover:bg-emerald-500/10 flex items-center gap-1"
                      data-testid={`lead-wame-${l.id}`}
                      title="Abrir WhatsApp com mensagem pré-preenchida"
                    >
                      <WhatsappLogo size={12} weight="fill" /> wa.me
                    </a>
                  )}
                </div>
              </label>
            ))}
          </div>
          {pg.pages > 1 && (
            <div className="p-3 border-t border-[var(--border)]">
              <Paginator page={pg.page} pages={pg.pages} onChange={load} total={pg.total} />
            </div>
          )}
        </div>

        <div className="card-flat p-6 h-fit">
          <div className="label-caps mb-2">Nova campanha</div>
          <div className="font-display font-bold text-xl mb-4">Disparo</div>
          <div className="text-xs text-white/50 mb-4">
            {selected.length} de {leads.length} leads selecionados nesta página
          </div>

          <div className="mb-4">
            <label className="label-caps block mb-2">Canal</label>
            <div className="grid grid-cols-4 gap-2 text-xs">
              {[
                { v: "email", l: "E-mail", i: EnvelopeSimple },
                { v: "whatsapp", l: "WhatsApp", i: WhatsappLogo },
                { v: "telegram", l: "Telegram", i: TelegramLogo },
                { v: "all", l: "Todos", i: Broadcast },
              ].map((c) => {
                const IconC = c.i;
                return (
                  <button
                    key={c.v}
                    onClick={() => setChannel(c.v)}
                    className={`border rounded-sm py-2 flex flex-col items-center gap-1 ${
                      channel === c.v
                        ? "border-[var(--accent)] text-[var(--accent)]"
                        : "border-[var(--border)] text-white/60"
                    }`}
                    data-testid={`channel-${c.v}`}
                  >
                    <IconC size={16} /> {c.l}
                  </button>
                );
              })}
            </div>
          </div>

          {(channel === "email" || channel === "all") && (
            <div className="mb-4">
              <label className="label-caps block mb-2">Assunto (email)</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="campaign-subject"
                className={inp}
              />
            </div>
          )}

          {channel === "whatsapp" && (
            <div className="mb-4">
              <label className="label-caps block mb-2">Modo WhatsApp</label>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <button
                  onClick={() => setWaMode("baileys")}
                  className={`border rounded-sm py-2 px-3 text-left ${waMode === "baileys" ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] text-white/60"}`}
                  data-testid="wa-mode-baileys"
                >
                  <div className="font-semibold">Automático (QR)</div>
                  <div className="text-[10px] opacity-70">Envia sozinho via sua sessão conectada</div>
                </button>
                <button
                  onClick={() => setWaMode("wame")}
                  className={`border rounded-sm py-2 px-3 text-left ${waMode === "wame" ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] text-white/60"}`}
                  data-testid="wa-mode-wame"
                >
                  <div className="font-semibold">Fila wa.me (5s)</div>
                  <div className="text-[10px] opacity-70">Abre WhatsApp Web, você clica Enviar</div>
                </button>
              </div>
              {waMode === "wame" && (
                <div className="text-[11px] text-amber-400/80 mt-2">
                  ⚠️ O navegador vai pedir permissão para abrir várias abas. Permita popups deste site.
                </div>
              )}
              <div className="text-[11px] opacity-60 mt-2">
                Variáveis suportadas: <code>{"{nome}"}</code>, <code>{"{email}"}</code>, <code>{"{telefone}"}</code>
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="label-caps block mb-2">Mensagem</label>
            <textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="campaign-message"
              className={inp}
              placeholder="Digite sua mensagem..."
            />
          </div>

          <button
            onClick={send}
            disabled={sending}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="campaign-send"
          >
            <PaperPlaneTilt size={18} weight="fill" /> {sending ? "Enviando..." : "Disparar campanha"}
          </button>
        </div>
      </div>

      {queue && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="wa-queue-modal">
          <div className="w-full max-w-md bg-[var(--bg-elev)] border border-[var(--border)] rounded-sm p-6 relative">
            <button
              onClick={stopQueue}
              className="absolute top-3 right-3 opacity-60 hover:opacity-100"
              data-testid="wa-queue-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="label-caps mb-2">Fila WhatsApp (wa.me)</div>
            <div className="text-2xl font-black tracking-tight mb-1">
              Enviando {queue.idx + 1} de {queue.items.length}
            </div>
            <div className="text-sm opacity-80 mb-3">
              <span className="font-semibold">{queue.items[queue.idx]?.lead?.name}</span>
              <span className="opacity-60"> · {queue.items[queue.idx]?.lead?.phone}</span>
            </div>
            <div className="w-full h-2 bg-white/5 rounded-sm overflow-hidden mb-4">
              <div
                className="h-2 bg-[var(--accent)] transition-all"
                style={{ width: `${((queue.idx + 1) / queue.items.length) * 100}%` }}
              />
            </div>
            <div className="text-xs opacity-70 mb-4">
              A próxima aba abre em ~{queue.delayMs / 1000}s. Clique em <b>Enviar</b> no WhatsApp que abrir.
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={queue.items[queue.idx]?.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 min-w-[120px] px-3 py-2 bg-[var(--accent)] text-black rounded-sm text-xs font-semibold flex items-center justify-center gap-1"
                data-testid="wa-queue-reopen"
              >
                <ExternalLink className="w-3 h-3" /> Reabrir atual
              </a>
              {queue.paused ? (
                <button
                  onClick={resumeQueue}
                  className="px-3 py-2 border border-[var(--border)] rounded-sm text-xs flex items-center gap-1 hover:bg-[var(--bg-base)]"
                  data-testid="wa-queue-resume"
                >
                  <Play className="w-3 h-3" /> Retomar
                </button>
              ) : (
                <button
                  onClick={pauseQueue}
                  className="px-3 py-2 border border-[var(--border)] rounded-sm text-xs flex items-center gap-1 hover:bg-[var(--bg-base)]"
                  data-testid="wa-queue-pause"
                >
                  <Pause className="w-3 h-3" /> Pausar
                </button>
              )}
              <button
                onClick={skipQueue}
                className="px-3 py-2 border border-[var(--border)] rounded-sm text-xs flex items-center gap-1 hover:bg-[var(--bg-base)]"
                data-testid="wa-queue-skip"
              >
                <SkipForward className="w-3 h-3" /> Próximo
              </button>
              <button
                onClick={stopQueue}
                className="px-3 py-2 border border-red-500/40 text-red-400 rounded-sm text-xs flex items-center gap-1 hover:bg-red-500/10"
                data-testid="wa-queue-stop"
              >
                <StopCircle className="w-3 h-3" /> Parar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
